import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query'
import { clientService } from '@/services/ClientService'
import { clientKeys, proposalKeys } from '@/hooks/queryKeys'
import type { ContactDto } from '@lib/dto/contact.dto'
import type {
  CreateContactInput,
  UpdateContactInput,
} from '@lib/input/contact.input'

/**
 * Contact write operations. Every one invalidates the owning client's detail
 * (contacts are mostly read through it), the client lists (which carry a
 * contact count), and proposals, which name the primary contact as the person
 * they go to.
 *
 * Note each mutation takes the `clientId` explicitly rather than reading it off
 * the response: promoting a new primary contact silently demotes another, so
 * the whole client has to be refetched, not just the row that came back.
 */
function useInvalidateClient(): (clientId: string) => void {
  const queryClient = useQueryClient()
  return (clientId: string) => {
    void queryClient.invalidateQueries({ queryKey: clientKeys.detail(clientId) })
    void queryClient.invalidateQueries({ queryKey: clientKeys.lists() })
    void queryClient.invalidateQueries({ queryKey: proposalKeys.all })
  }
}

export interface AddContactVariables {
  clientId: string
  input: CreateContactInput
}

export function useAddContact(): UseMutationResult<
  ContactDto,
  Error,
  AddContactVariables
> {
  const invalidate = useInvalidateClient()
  return useMutation({
    mutationFn: ({ clientId, input }: AddContactVariables) =>
      clientService.addContact(clientId, input),
    onSuccess: (_contact, variables) => invalidate(variables.clientId),
  })
}

export interface UpdateContactVariables {
  id: string
  clientId: string
  input: UpdateContactInput
}

export function useUpdateContact(): UseMutationResult<
  ContactDto,
  Error,
  UpdateContactVariables
> {
  const invalidate = useInvalidateClient()
  return useMutation({
    mutationFn: ({ id, input }: UpdateContactVariables) =>
      clientService.updateContact(id, input),
    onSuccess: (_contact, variables) => invalidate(variables.clientId),
  })
}

export interface ArchiveContactVariables {
  id: string
  clientId: string
  archived: boolean
}

export function useArchiveContact(): UseMutationResult<
  ContactDto,
  Error,
  ArchiveContactVariables
> {
  const invalidate = useInvalidateClient()
  return useMutation({
    mutationFn: ({ id, archived }: ArchiveContactVariables) =>
      clientService.setContactArchived(id, archived),
    onSuccess: (_contact, variables) => invalidate(variables.clientId),
  })
}
